/**
 * components/riskTerminal/liveStream.js
 *
 * upstox-054 — ONE server-sent-events connection per page to GET
 * /upstox/stream, shared by usePanel and useWatchlist. The server sends:
 *   panel      the whole panel (a refresh, an action, a grading run)
 *   patch      the panel's changed top-level keys (Kite re-pricing, ≤ 1/s)
 *   watchlist  the whole watchlist (≤ 1/s while prices move)
 *   signin     { needed } when it changes
 *   heartbeat  every 15 s of silence
 *   reconnect  a planned close before Railway's 15-minute request cap
 *
 * It stays open in hidden tabs, so the screen is current on return.
 * States (`state.mode`): connecting → live; on a drop: reconnecting (back-off
 * 1, 2, 4, 8, 15 s); after FALLBACK_AFTER_MS without a stream: polling — the
 * hooks poll the old endpoints while it keeps trying, until the stream
 * returns. A live stream silent for SILENCE_MS (no heartbeat) is a drop.
 */

import { withAccount } from './accounts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const RECONNECT_STEPS_MS = [1000, 2000, 4000, 8000, 15000];
export const FALLBACK_AFTER_MS = 30000;
export const SILENCE_MS = 40000;
const EVENTS = ['panel', 'patch', 'watchlist', 'signin', 'heartbeat'];

let es = null;
let subs = new Set();
let state = { mode: 'connecting', downSince: null, lastEventAt: null };
let attempt = 0;
let retryTimer = null;
let watchdog = null;

function setState(patch) {
  state = { ...state, ...patch };
  subs.forEach(s => s.state && s.state(state));
}

function emit(name, data) {
  state = { ...state, lastEventAt: Date.now() };
  subs.forEach(s => s[name] && s[name](data));
}

function dropped() {
  if (es) { es.close(); es = null; }
  if (!subs.size) return;
  const now = Date.now();
  const downSince = state.downSince || now;
  setState({ mode: now - downSince >= FALLBACK_AFTER_MS ? 'polling' : 'reconnecting', downSince });
  const wait = RECONNECT_STEPS_MS[Math.min(attempt, RECONNECT_STEPS_MS.length - 1)];
  attempt += 1;
  clearTimeout(retryTimer);
  retryTimer = setTimeout(open, wait);
}

function open() {
  if (!subs.size || typeof window === 'undefined' || !window.EventSource) {
    if (subs.size) setState({ mode: 'polling', downSince: state.downSince || Date.now() });
    return;
  }
  if (es) es.close();
  const src = new window.EventSource(`${API}/upstox${withAccount('/stream')}`, { withCredentials: true });
  es = src;
  src.onopen = () => { attempt = 0; setState({ mode: 'live', downSince: null, lastEventAt: Date.now() }); };
  src.onerror = () => { if (es === src) dropped(); };
  EVENTS.forEach(name => src.addEventListener(name, e => {
    if (es !== src) return;                              // a stream we have replaced says nothing
    let data = null;
    try { data = JSON.parse(e.data); } catch (err) { return; }
    if (state.mode !== 'live') { attempt = 0; setState({ mode: 'live', downSince: null }); }
    emit(name, data);
  }));
  // the server's planned close: straight back, no "reconnecting" shown
  src.addEventListener('reconnect', () => { if (es === src) { src.close(); es = null; open(); } });
}

/** Subscribe { panel, patch, watchlist, signin, heartbeat, state } → unsubscribe. */
export function subscribe(handlers) {
  subs.add(handlers);
  if (handlers.state) handlers.state(state);
  if (subs.size === 1) {
    attempt = 0;
    setState({ mode: 'connecting', downSince: Date.now(), lastEventAt: null });
    open();
    watchdog = setInterval(() => {
      if (state.mode === 'live' && state.lastEventAt && Date.now() - state.lastEventAt > SILENCE_MS) dropped();
      else if (state.mode === 'reconnecting' && state.downSince && Date.now() - state.downSince >= FALLBACK_AFTER_MS) setState({ mode: 'polling' });
    }, 5000);
  }
  return () => {
    subs.delete(handlers);
    if (!subs.size) {
      clearTimeout(retryTimer); clearInterval(watchdog);
      if (es) { es.close(); es = null; }
    }
  };
}

/**
 * upstox-067: the account changed — close the stream for the old one and open one for the
 * new one (accounts.js already holds the new id). Nothing from the old stream is delivered
 * after this returns: its handlers are detached before it is closed.
 */
export function restart() {
  clearTimeout(retryTimer);
  if (es) { const old = es; es = null; old.onopen = old.onerror = null; old.close(); }
  if (!subs.size) return;
  attempt = 0;
  setState({ mode: 'connecting', downSince: Date.now(), lastEventAt: null });
  open();
}

export function currentState() { return state; }

/** The status-bar wording for a stream state (null when live). */
export function streamFault(s) {
  if (!s || s.mode === 'live') return null;
  if (s.mode === 'polling') return { name: 'STREAM', v: 'DOWN · POLLING EVERY 5 S', state: 'stale' };
  if (s.mode === 'reconnecting') return { name: 'STREAM', v: 'RECONNECTING…', state: 'stale' };
  return null;                                   // the first connect: no fault yet
}
